import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

import { Field, Provider } from '../../../api-clients/oem-connection';
import { DialogService } from '../../../services/dialog.service';
import { OemIntegrationService } from '../../../services/oem-integration.service';
import { crossClaimedBy } from '../../../utils/cross-provider-link';
import { PROVIDER_LABELS } from '../integrations.component';

interface RowState {
  /**
   * Selected Harvest Mate farm `ext_id` (UUID) — drives the cascading block
   * dropdown. The blocks endpoint resolves the path id against `Farm.ext_id`
   * (a UUID column), NOT the numeric PK — passing the numeric id 500s
   * (DataError, invalid UUID).
   */
  farmExtId?: string;
  /** Selected Harvest Mate block id (numeric), sent to the link call. */
  blockIdInput?: number;
  /** Blocks of the selected farm (loaded on farm change). */
  blocks: HmBlock[];
  loadingBlocks: boolean;
  busy: boolean;
}

/** Minimal shape of a Harvest Mate farm for the cascade picker. */
interface HmFarm {
  /**
   * Numeric Harvest Mate farm id — used only to resolve the OEM-farm→HM-farm
   * linkage when pre-filling a field row (the farm-link panel persists the
   * linkage by numeric id). The cascade picker itself keys on `extId`.
   */
  id: number;
  /** UUID — the identifier the blocks endpoint expects. */
  extId: string;
  name: string;
  /** Owner (grower) display name — drives the <optgroup> heading (SRA003-502). */
  growerName: string | null;
  /**
   * Annotation when this HM farm is already linked to an OEM farm, or null when
   * free. Set for the OTHER vendor ("already linked to Case New Holland") AND
   * this same vendor ("already linked") — an HM farm maps to one OEM farm, so a
   * taken one is disabled + annotated. Mirrors the OEM Farms picker; the
   * row-level template still allows the row's OWN pre-filled farm (SRA003-473 +
   * same-provider double-map guard).
   */
  linkedLabel: string | null;
}

/** HM farms grouped by grower for the picker's <optgroup>s (SRA003-502). */
interface HmFarmGroup {
  growerName: string;
  farms: HmFarm[];
}

/** One table group: all fields (linked + unlinked) sharing a parent OEM farm. */
export interface FieldGroup {
  /** Parent OEM farm id, or null for fields the OEM gave no farm. */
  farmId: string | null;
  /** Display heading for the group (the OEM farm name). */
  farmName: string;
  /** The Harvest Mate farm this OEM farm is linked to, or null when unlinked. */
  linkedHmFarmName: string | null;
  fields: Field[];
}

/** Minimal shape of a Harvest Mate block for the cascade picker. */
interface HmBlock {
  id: number;
  block_number: string;
  /**
   * Label of the OTHER provider already claiming this block (SRA003-473), or
   * null when it's free to link from this panel. When set, the block option is
   * disabled and annotated — cross-vendor links are blocked, not silently
   * omitted.
   */
  crossClaimedLabel: string | null;
}

@Component({
  selector: 'app-field-link-panel',
  templateUrl: './field-link-panel.component.html',
  styleUrls: ['./field-link-panel.component.css'],
  standalone: false,
})
export class FieldLinkPanelComponent implements OnInit, OnChanges, OnDestroy {
  @Input() provider!: Provider;
  @Input() providerLabel = '';

  loading = false;
  errorMessage = '';
  /**
   * Set when the BE returns 503 with `cnh_unavailable` (the CNH FieldOps base
   * URL hasn't been confirmed yet). Surfaced as a banner per HANDOFF §3.4 -
   * NOT a toast.
   */
  cnhUnavailable = false;

  fields: Field[] = [];
  hmFarms: HmFarm[] = [];
  /** HM farms grouped by grower for the picker's <optgroup>s (SRA003-502). */
  farmGroups: HmFarmGroup[] = [];
  rowState: Record<string, RowState> = {};
  /**
   * OEM-farm id → linked Harvest Mate farm numeric id, built from the farm-link
   * panel's data (`GET .../farms`). Drives the pre-fill of each field row's
   * Harvest Mate farm so a contractor who already linked the parent farm only
   * has to pick the block (SRA003-502). Empty when farms can't be loaded —
   * pre-fill is then simply skipped, the panel still works.
   */
  private linkedHmFarmIdByOemFarmId = new Map<string, number>();
  /** Subscription to the farm-linkage change signal (SRA003-502). */
  private farmLinksSub?: Subscription;
  /**
   * Shared, deduped block lookups keyed by HM farm `ext_id` for one load cycle.
   * Pre-filling N field rows that share a parent OEM farm would otherwise fan
   * out to N identical `listLinkableBlocks` calls; this collapses them to one
   * per farm. Cleared at the start of each `loadFields()` so a link/unlink
   * reload re-fetches fresh `linked_provider` stamps (no cross-reload staleness).
   */
  private blocksByExtId = new Map<string, Observable<HmBlock[]>>();

  constructor(
    private oemSvc: OemIntegrationService,
    private dialog: DialogService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    // SRA003-502: when the sibling farm panel links/unlinks a farm, re-run the
    // pre-fill so field rows reflect the new linkage (newly-linked farm fills
    // its fields' HM farm; an unlinked farm clears them back to blank).
    this.farmLinksSub = this.oemSvc.farmLinksRefresh$.subscribe(() => {
      if (this.provider) this.loadLookupsThenFields();
    });
  }

  ngOnChanges(_changes: SimpleChanges): void {
    if (!this.provider) return;
    this.loadLookupsThenFields();
  }

  ngOnDestroy(): void {
    this.farmLinksSub?.unsubscribe();
  }

  /**
   * Load the two pre-fill lookup sources — Harvest Mate farms (for the cascade
   * picker) and the OEM-farm→HM-farm linkage — THEN load the fields, so a
   * field row's Harvest Mate farm can be pre-filled from the parent farm's
   * existing linkage. Both lookups are best-effort: a failure leaves the map
   * empty and pre-fill is skipped, but field linking still works (the
   * contractor just picks the farm manually).
   */
  loadLookupsThenFields(): void {
    forkJoin({
      hm: this.oemSvc.listLinkableFarms().pipe(catchError(() => of(null))),
      oem: this.oemSvc.listFarms(this.provider).pipe(catchError(() => of(null))),
    }).subscribe(({ hm, oem }) => {
      const hmRows = (hm as { farms?: any[] } | null)?.farms ?? [];
      this.hmFarms = hmRows
        .filter((f) => f?.status === 'ACTIVE' && f?.ext_id && f?.id != null)
        .map((f) => ({
          id: f.id,
          extId: f.ext_id,
          name: f.name,
          growerName: f.grower_name,
          linkedLabel: this.hmFarmLinkedLabel(f.linked_provider),
        }));
      this.farmGroups = this.groupByGrower(this.hmFarms);

      this.linkedHmFarmIdByOemFarmId = new Map();
      for (const farm of (oem as any)?.farms ?? []) {
        if (farm?.oem_farm_id && farm?.linked_farm_id != null) {
          this.linkedHmFarmIdByOemFarmId.set(
            farm.oem_farm_id,
            farm.linked_farm_id,
          );
        }
      }

      this.loadFields();
    });
  }

  /** Group HM farms by grower name (sorted; owner-less → "Other"). */
  private groupByGrower(farms: HmFarm[]): HmFarmGroup[] {
    const byName = new Map<string, HmFarm[]>();
    for (const f of farms) {
      const key = f.growerName || 'Other';
      const list = byName.get(key);
      if (list) list.push(f);
      else byName.set(key, [f]);
    }
    return [...byName.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([growerName, gFarms]) => ({ growerName, farms: gFarms }));
  }

  /**
   * Load the blocks of the row's selected farm (cascade step 2). Resets any
   * previously-chosen block so a stale block id can't be sent after a farm
   * change. Block targets only — a field can never offer a farm/harvester.
   *
   * Fetches by farm `ext_id` (UUID) via
   * `GET /oem-connection/linkable-farms/{ext_id}/blocks` (SRA003-502) — the
   * contractor-scoped sibling of the owner-scoped `/farms/{ext_id}/blocks`, so
   * grower-farm blocks resolve too. A stale-response guard ignores a slow reply
   * once the row's selected farm has changed (fast farm-switch race).
   */
  onFarmChange(field: Field): void {
    const state = this.rowFor(field);
    state.blockIdInput = undefined;
    state.blocks = [];
    if (!state.farmExtId) return;
    const requestedExtId = state.farmExtId;
    state.loadingBlocks = true;
    this.blocksForExtId(requestedExtId).subscribe({
      next: (blocks) => {
        if (state.farmExtId !== requestedExtId) return; // stale — farm changed
        state.blocks = blocks;
        state.loadingBlocks = false;
      },
      error: () => {
        if (state.farmExtId !== requestedExtId) return; // stale — farm changed
        state.blocks = [];
        state.loadingBlocks = false;
      },
    });
  }

  /**
   * Block lookup for one HM farm `ext_id`, shared across every row that selects
   * the same farm in a load cycle (SRA003-502). The first caller triggers the
   * single HTTP request; concurrent pre-fill rows of the same parent farm reuse
   * the in-flight `shareReplay`'d observable → 1 call per farm, not N. The
   * cache is reset on each `loadFields()` so stamps never go stale.
   */
  private blocksForExtId(extId: string): Observable<HmBlock[]> {
    let blocks$ = this.blocksByExtId.get(extId);
    if (!blocks$) {
      blocks$ = this.oemSvc.listLinkableBlocks(extId).pipe(
        map((res: any) =>
          ((res?.blocks ?? []) as any[])
            .filter((b) => b?.status === 'ACTIVE' && b?.id != null)
            .map((b) => ({
              id: b.id,
              block_number: b.block_number,
              crossClaimedLabel: this.crossClaimLabel(b.linked_provider),
            })),
        ),
        shareReplay(1),
      );
      this.blocksByExtId.set(extId, blocks$);
    }
    return blocks$;
  }

  loadFields(): void {
    this.loading = true;
    this.errorMessage = '';
    this.cnhUnavailable = false;
    this.fields = [];
    this.rowState = {};
    // Fresh load cycle — drop last cycle's shared block lookups so a
    // link/unlink reload re-fetches current linked_provider stamps.
    this.blocksByExtId.clear();
    this.oemSvc.listFields(this.provider).subscribe({
      next: (res) => {
        this.fields = res?.fields ?? [];
        this.rowState = {};
        for (const f of this.fields) {
          this.rowState[f.oem_field_id] = {
            busy: false,
            blocks: [],
            loadingBlocks: false,
          };
        }
        this.prefillFarms();
        this.loading = false;
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        this.fields = [];
        this.rowState = {};
        if (this.isCnhUnavailable(err)) {
          this.cnhUnavailable = true;
        } else {
          this.errorMessage = `Could not load ${this.providerLabel} fields`;
        }
      },
    });
  }

  rowFor(field: Field): RowState {
    let state = this.rowState[field.oem_field_id];
    if (!state) {
      state = { busy: false, blocks: [], loadingBlocks: false };
      this.rowState[field.oem_field_id] = state;
    }
    return state;
  }

  /**
   * Display label of the provider cross-claiming an HM block (SRA003-473), or
   * null when it's free to link from this panel.
   */
  private crossClaimLabel(linkedProvider: unknown): string | null {
    const other = crossClaimedBy(linkedProvider, this.provider);
    return other ? PROVIDER_LABELS[other] : null;
  }

  /**
   * Annotation for an HM farm already linked to an OEM farm — non-null also
   * disables the farm picker option so an HM farm can't be mapped to two OEM
   * farms. "already linked to <vendor>" when the OTHER vendor claims it, or just
   * "already linked" when this same provider already linked it to a different
   * OEM farm. Null = free. The row template still permits the row's OWN
   * pre-filled farm (see the picker).
   */
  private hmFarmLinkedLabel(linkedProvider: unknown): string | null {
    const other = crossClaimedBy(linkedProvider, this.provider);
    if (other) return `already linked to ${PROVIDER_LABELS[other]}`;
    if (linkedProvider === this.provider) return 'already linked';
    return null;
  }

  isLinked(f: Field): boolean {
    return f.linked_block_id != null;
  }

  /**
   * SRA003-524: this OEM field is already linked by ANOTHER contractor in the
   * org (the BE org-wide overlay flag). The picker disables it — linking would
   * 409 — without revealing who holds it (privacy-aware: boolean only).
   */
  isLinkedElsewhere(f: Field): boolean {
    return !this.isLinked(f) && f.linked_elsewhere === true;
  }

  /**
   * All fields grouped by their parent OEM farm (SRA003-502), preserving the
   * BE's field order within and across groups. Each group carries the farm's
   * current Harvest Mate linkage (for the group header). Fields the OEM gave no
   * farm fall into a trailing "Unknown farm" group so they're never dropped.
   * Linked and unlinked fields sit together in one group — the unified table
   * renders a per-row Link/Unlink action off `isLinked()`.
   */
  fieldGroups(): FieldGroup[] {
    const groups: FieldGroup[] = [];
    const byKey = new Map<string, FieldGroup>();
    for (const f of this.fields) {
      const farmId = f.farm_id ?? null;
      const key = farmId ?? '__none__';
      let group = byKey.get(key);
      if (!group) {
        group = {
          farmId,
          farmName: f.farm_name || (farmId ? farmId : 'Unknown farm'),
          linkedHmFarmName: this.linkedHmFarmName(farmId),
          fields: [],
        };
        byKey.set(key, group);
        groups.push(group);
      }
      group.fields.push(f);
    }
    return groups;
  }

  /** Name of the HM farm an OEM farm is linked to, or null when unlinked. */
  private linkedHmFarmName(oemFarmId: string | null): string | null {
    if (!oemFarmId) return null;
    const hmFarmId = this.linkedHmFarmIdByOemFarmId.get(oemFarmId);
    if (hmFarmId == null) return null;
    return this.hmFarms.find((hm) => hm.id === hmFarmId)?.name ?? null;
  }

  /**
   * True when the field's parent OEM farm is already linked — the row's
   * Harvest Mate farm picker is then locked to that farm (SRA003-502 1:1), so
   * all fields of one OEM farm map to blocks of the same HM farm. Before the
   * farm is linked the picker is free (the field-first entry point).
   */
  isFarmLocked(field: Field): boolean {
    return !!field.farm_id && this.linkedHmFarmIdByOemFarmId.has(field.farm_id);
  }

  /** Numeric HM farm id for a cascade `extId` (the picker keys on extId). */
  private hmFarmIdForExtId(extId?: string): number | undefined {
    if (!extId) return undefined;
    return this.hmFarms.find((hm) => hm.extId === extId)?.id;
  }

  /**
   * Pre-fill each available field row's Harvest Mate farm from the parent OEM
   * farm's existing linkage, then load that farm's blocks so the contractor
   * only picks a block (SRA003-502). No-op for fields whose parent farm isn't
   * linked yet (or has no HM farm we can resolve) — those rows stay blank.
   */
  private prefillFarms(): void {
    for (const f of this.fields) {
      if (this.isLinked(f)) continue;
      const extId = this.prefilledExtIdForField(f);
      if (!extId) continue;
      const state = this.rowFor(f);
      state.farmExtId = extId;
      this.onFarmChange(f);
    }
  }

  /**
   * Resolve the HM farm `extId` to pre-fill for a field: its parent OEM farm →
   * the linked HM farm numeric id (from the farm linkage) → that HM farm's
   * `extId` (the cascade picker's key). Undefined when any hop is missing.
   */
  private prefilledExtIdForField(field: Field): string | undefined {
    const oemFarmId = field.farm_id;
    if (!oemFarmId) return undefined;
    const hmFarmId = this.linkedHmFarmIdByOemFarmId.get(oemFarmId);
    if (hmFarmId == null) return undefined;
    return this.hmFarms.find((hm) => hm.id === hmFarmId)?.extId;
  }

  link(field: Field): void {
    const state = this.rowFor(field);
    const blockId = state.blockIdInput;
    if (blockId == null || !Number.isFinite(blockId) || blockId <= 0) {
      this.toastr.warning('Select a Harvest Mate farm and block to link to');
      return;
    }
    // SRA003-502 (1:1): a field can only link to a block in the HM farm its
    // parent OEM farm is linked to. The picker is locked once the farm is
    // linked; this guards against a race slipping a mismatch past the lock.
    const selectedHmFarmId = this.hmFarmIdForExtId(state.farmExtId);
    const existingHmFarmId = field.farm_id
      ? this.linkedHmFarmIdByOemFarmId.get(field.farm_id)
      : undefined;
    if (
      existingHmFarmId != null &&
      selectedHmFarmId != null &&
      existingHmFarmId !== selectedHmFarmId
    ) {
      this.toastr.error(
        `${field.farm_name || 'This farm'} is already linked to another Harvest Mate farm — link this field to a block in that farm.`,
      );
      return;
    }
    state.busy = true;
    this.oemSvc.linkField(this.provider, field.oem_field_id, blockId).subscribe({
      next: (link) => {
        const replayed = link?.replayed_count ?? 0;
        const fieldName = field.name || field.oem_field_id;
        const msg =
          replayed > 0
            ? `Linked ${fieldName} to block ${blockId}. Resolved ${replayed} unmapped event${replayed === 1 ? '' : 's'}.`
            : `Linked ${fieldName} to block ${blockId}`;
        this.toastr.success(msg);
        // Field-first: auto-link the parent OEM farm to the block's HM farm,
        // then refresh both panels.
        this.autoLinkParentFarm(field, selectedHmFarmId, () => {
          state.busy = false;
          this.refreshAfterChange();
        });
      },
      error: (err: HttpErrorResponse) => {
        state.busy = false;
        if (err?.status === 403) {
          // SRA003-517: the harvester_crop_assignments prereq surfaces as a
          // 403 (typed `block_not_assigned`). Scope the friendly copy to any
          // 403 on the link endpoint so a drift in the typed code still yields
          // the prereq guidance rather than the raw generic "Could not link".
          // The only other 403 here is the contractor role gate, unreachable
          // from this contractor-only panel.
          this.toastr.error(
            'You can link this field once this block has a crop assignment.',
          );
        } else if (
          err?.status === 409 &&
          err?.error?.errorType === 'already_linked' &&
          err?.error?.errorMessage
        ) {
          // SRA003-516: reject-don't-replace. The BE returns 409
          // errorType="already_linked" with a fully-composed friendly
          // errorMessage naming the incumbent ("This block is already linked to
          // CaseIH (xxx). Unlink it first." — CNH renders as "CaseIH"). This
          // now covers BOTH same- and cross-vendor conflicts, superseding the
          // old field_already_linked and cross_provider_link_conflict branches.
          // Show it verbatim — the incumbent is baked into the string. Note the
          // envelope key is errorType (camelCase), which matchesError's
          // snake_case code/reason/error_type won't catch. The generic else
          // below is the fallback when errorMessage is absent.
          this.toastr.error(err.error.errorMessage);
        } else {
          this.toastr.error(
            `Could not link ${field.name || field.oem_field_id}`,
          );
        }
      },
    });
  }

  unlink(field: Field): void {
    this.dialog
      .confirmDialog({
        title: 'Unlink field',
        message: `Unlink ${field.name || field.oem_field_id} from its Harvest Mate block? Telemetry will stop being attributed to that block until you re-link.`,
        confirmCaption: 'Unlink',
        cancelCaption: 'Cancel',
      })
      .subscribe((yes) => {
        if (!yes) return;
        const state = this.rowFor(field);
        state.busy = true;
        this.oemSvc.unlinkField(this.provider, field.oem_field_id).subscribe({
          next: () => {
            this.toastr.success(`Unlinked ${field.name || field.oem_field_id}`);
            // Field-first: if this was the farm's last linked field, unlink the
            // farm too, then refresh both panels.
            this.autoUnlinkParentFarmIfEmpty(field, () => {
              state.busy = false;
              this.refreshAfterChange();
            });
          },
          error: () => {
            state.busy = false;
            this.toastr.error(
              `Could not unlink ${field.name || field.oem_field_id}`,
            );
          },
        });
      });
  }

  /**
   * Field-first (SRA003-502): ensure the field's parent OEM farm is linked to
   * the block's HM farm. No-op when the field has no parent farm or the farm is
   * already linked. Best-effort — a farm-link failure (e.g. `farm_not_assigned`)
   * leaves the field linked and surfaces a soft warning rather than rolling back.
   */
  private autoLinkParentFarm(
    field: Field,
    hmFarmId: number | undefined,
    done: () => void,
  ): void {
    const oemFarmId = field.farm_id;
    if (
      !oemFarmId ||
      hmFarmId == null ||
      this.linkedHmFarmIdByOemFarmId.has(oemFarmId)
    ) {
      done();
      return;
    }
    this.oemSvc.linkFarm(this.provider, oemFarmId, hmFarmId).subscribe({
      next: () => done(),
      error: () => {
        this.toastr.warning(
          `${field.name || 'Field'} linked, but its farm couldn't be linked automatically — link it in the OEM Farms section.`,
        );
        done();
      },
    });
  }

  /**
   * Field-first (SRA003-502): when the just-unlinked field was the LAST linked
   * field of its parent OEM farm, unlink the farm too (relaxed rule — origin is
   * not tracked, so this removes auto- and manually-created farm links alike).
   * Counts from the in-memory fields, excluding the field we just unlinked (its
   * row is still stale-linked until the reload). Best-effort.
   */
  private autoUnlinkParentFarmIfEmpty(field: Field, done: () => void): void {
    const oemFarmId = field.farm_id;
    if (!oemFarmId || !this.linkedHmFarmIdByOemFarmId.has(oemFarmId)) {
      done();
      return;
    }
    const stillLinked = this.fields.some(
      (f) =>
        f.farm_id === oemFarmId &&
        f.oem_field_id !== field.oem_field_id &&
        this.isLinked(f),
    );
    if (stillLinked) {
      done();
      return;
    }
    this.oemSvc.unlinkFarm(this.provider, oemFarmId).subscribe({
      next: () => done(),
      error: () => {
        this.toastr.warning(
          `${field.name || 'Field'} unlinked, but its farm couldn't be unlinked automatically.`,
        );
        done();
      },
    });
  }

  /**
   * Reload this panel (re-fetches the farm map → updates the 1:1 lock +
   * pre-fill), refresh the parent connections view, and notify the sibling
   * farm panel that field-driven farm links may have changed (SRA003-502).
   */
  private refreshAfterChange(): void {
    this.loadLookupsThenFields();
    this.oemSvc.requestRefresh();
    this.oemSvc.notifyFieldLinksChanged();
  }

  /**
   * The OpenAPI ModelError envelope uses `code` / `reason`; HANDOFF docs the
   * same conditions as `error_type`. Match defensively across all three keys
   * so a future BE rename in either direction doesn't silently drop the
   * specific toast. If the body isn't parseable JSON, return false — the
   * caller falls back to the generic toast/error rather than mis-attributing
   * an infrastructure failure to a specific BE-typed reason.
   */
  private matchesError(
    err: HttpErrorResponse,
    status: number,
    expected: string,
  ): boolean {
    if (err?.status !== status) return false;
    const body = err?.error;
    if (!body || typeof body !== 'object') return false;
    return (
      body.code === expected ||
      body.reason === expected ||
      body.error_type === expected
    );
  }

  private isCnhUnavailable(err: HttpErrorResponse): boolean {
    if (this.provider !== Provider.Cnh) return false;
    return this.matchesError(err, 503, 'cnh_unavailable');
  }
}
